import { useState } from 'react';
import { CheckCircle2, Clock, AlertCircle, FileText, Download, Bell, Lock, Calendar, Users, Building } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

export default function PresidenteFlow() {
  const { lang } = useLanguage();
  const isCA = lang === 'ca';

  const L = isCA
    ? {
        title: 'Portal del president',
        sub: 'C/ del Mar 14 · Comunitat de propietaris',
        readonly: 'Només lectura',
        kpis: {
          open: 'Incidències obertes',
          resolved30: 'Resoltes (30 dies)',
          docs: 'Documents',
          avisos: 'Avisos actius',
        },
        tabIncidents: 'Incidències',
        tabAvisos: 'Avisos',
        tabDocs: 'Documents',
        ref: 'Ref',
        status: { abierta: 'Oberta', en_curso: 'En curs', resuelta: 'Resolta' },
        avisos: [
          { title: 'Tall d’aigua programat', date: '24 Gen 2026', body: 'Dilluns 24 de gener, de 10:00 a 13:00. Manteniment del dipòsit.', tag: 'Manteniment' },
          { title: 'Reunió de junta', date: '18 Gen 2026', body: 'Junta ordinària dimecres 18 a les 19:30 al hall del portal.', tag: 'Junta' },
        ],
        docs: [
          { name: 'Acta junta ordinària 2025', size: '420 KB', date: 'Des 2025' },
          { name: 'Pòlissa multirisc 2026', size: '1.2 MB', date: 'Gen 2026' },
          { name: 'Normativa comunitat', size: '180 KB', date: 'Set 2024' },
          { name: 'Pressupost 2026', size: '95 KB', date: 'Des 2025' },
        ],
        incidents: [
          { id: 'INC-1041', titulo: 'Bombeta portal fosa', estado: 'abierta', fecha: 'fa 2 h' },
          { id: 'INC-1038', titulo: 'Neteja escala', estado: 'resuelta', fecha: 'fa 2 dies' },
          { id: 'INC-1032', titulo: 'Aixeta jardí degotant', estado: 'resuelta', fecha: 'fa 1 setmana' },
          { id: 'INC-1024', titulo: 'Llum garatge defectuosa', estado: 'resuelta', fecha: 'fa 2 setmanes' },
        ],
        download: 'Descarregar',
      }
    : {
        title: 'Portal del presidente',
        sub: 'C/ del Mar 14 · Comunidad de propietarios',
        readonly: 'Solo lectura',
        kpis: {
          open: 'Incidencias abiertas',
          resolved30: 'Resueltas (30 días)',
          docs: 'Documentos',
          avisos: 'Avisos activos',
        },
        tabIncidents: 'Incidencias',
        tabAvisos: 'Avisos',
        tabDocs: 'Documentos',
        ref: 'Ref',
        status: { abierta: 'Abierta', en_curso: 'En curso', resuelta: 'Resuelta' },
        avisos: [
          { title: 'Corte de agua programado', date: '24 Ene 2026', body: 'El lunes 24 de enero, de 10:00 a 13:00. Mantenimiento del depósito.', tag: 'Mantenimiento' },
          { title: 'Junta ordinaria', date: '18 Ene 2026', body: 'Junta ordinaria el miércoles 18 a las 19:30 en el portal.', tag: 'Junta' },
        ],
        docs: [
          { name: 'Acta junta ordinaria 2025', size: '420 KB', date: 'Dic 2025' },
          { name: 'Póliza multirriesgo 2026', size: '1.2 MB', date: 'Ene 2026' },
          { name: 'Normativa comunidad', size: '180 KB', date: 'Sep 2024' },
          { name: 'Presupuesto 2026', size: '95 KB', date: 'Dic 2025' },
        ],
        incidents: [
          { id: 'INC-1041', titulo: 'Bombilla portal fundida', estado: 'abierta', fecha: 'hace 2 h' },
          { id: 'INC-1038', titulo: 'Limpieza escalera', estado: 'resuelta', fecha: 'hace 2 días' },
          { id: 'INC-1032', titulo: 'Grifo jardín goteando', estado: 'resuelta', fecha: 'hace 1 semana' },
          { id: 'INC-1024', titulo: 'Luz garaje defectuosa', estado: 'resuelta', fecha: 'hace 2 semanas' },
        ],
        download: 'Descargar',
      };

  const [tab, setTab] = useState('incidents');

  return (
    <div className="bg-app min-h-[calc(100vh-73px)]" data-testid="presidente-flow">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 md:py-16">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Building size={16} className="text-secondary-app" />
              <span className="label-eyebrow">{L.sub}</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl text-app">{L.title}</h1>
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs bg-info text-white/95 px-3 py-1.5 rounded-full font-medium" style={{ background: '#2B6CB0' }}>
            <Lock size={12} /> {L.readonly}
          </span>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          <PKpi icon={AlertCircle} label={L.kpis.open} value={1} color="#8B5E2C" bg="#D4A37320" />
          <PKpi icon={CheckCircle2} label={L.kpis.resolved30} value={12} color="#3B7A57" bg="#3B7A5715" />
          <PKpi icon={FileText} label={L.kpis.docs} value={L.docs.length} color="#2B6CB0" bg="#2B6CB015" />
          <PKpi icon={Bell} label={L.kpis.avisos} value={L.avisos.length} color="#B34E3A" bg="#B34E3A15" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-surface border border-app rounded-md p-1 mb-6 max-w-md">
          {[
            ['incidents', L.tabIncidents],
            ['avisos', L.tabAvisos],
            ['docs', L.tabDocs],
          ].map(([k, v]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`flex-1 text-sm font-medium px-3 py-2 rounded transition-colors ${
                tab === k ? 'bg-primary-app text-white' : 'text-app-secondary hover:text-app'
              }`}
              data-testid={`pres-tab-${k}`}
            >
              {v}
            </button>
          ))}
        </div>

        {tab === 'incidents' && (
          <div className="space-y-2.5" data-testid="pres-incidents">
            {L.incidents.map((i) => {
              const cfg = {
                abierta: { bg: '#D4A37320', color: '#8B5E2C', icon: AlertCircle },
                en_curso: { bg: '#2B6CB015', color: '#2B6CB0', icon: Clock },
                resuelta: { bg: '#3B7A5715', color: '#3B7A57', icon: CheckCircle2 },
              }[i.estado];
              const Icon = cfg.icon;
              return (
                <div
                  key={i.id}
                  className="bg-surface border border-app rounded-md p-4 flex items-center justify-between gap-4 hover:border-primary-app/30 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-app-muted">{i.id}</span>
                      <span className="text-xs text-app-muted">·</span>
                      <span className="text-xs text-app-muted">{i.fecha}</span>
                    </div>
                    <div className="font-medium text-app">{i.titulo}</div>
                  </div>
                  <span
                    className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full flex-shrink-0"
                    style={{ background: cfg.bg, color: cfg.color }}
                  >
                    <Icon size={11} /> {L.status[i.estado]}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {tab === 'avisos' && (
          <div className="space-y-3" data-testid="pres-avisos">
            {L.avisos.map((a, idx) => (
              <div key={idx} className="bg-surface border border-app rounded-md p-5">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs bg-secondary-app/10 text-secondary-app font-medium px-2 py-0.5 rounded">
                        {a.tag}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-app-muted">
                        <Calendar size={11} /> {a.date}
                      </span>
                    </div>
                    <h3 className="font-serif text-xl text-app">{a.title}</h3>
                  </div>
                </div>
                <p className="text-sm text-app-secondary leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'docs' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pres-docs">
            {L.docs.map((d, idx) => (
              <div key={idx} className="bg-surface border border-app rounded-md p-4 flex items-center gap-4 hover:border-primary-app/30 transition-colors">
                <div className="w-10 h-10 rounded-md bg-secondary-app/10 flex items-center justify-center flex-shrink-0">
                  <FileText size={18} className="text-secondary-app" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-app text-sm truncate">{d.name}</div>
                  <div className="text-xs text-app-muted mt-0.5">
                    {d.date} · {d.size}
                  </div>
                </div>
                <button className="text-xs text-primary-app font-medium flex items-center gap-1 hover:underline">
                  <Download size={13} /> {L.download}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PKpi({ icon: Icon, label, value, color, bg }) {
  return (
    <div className="bg-surface border border-app rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-app-muted">{label}</span>
        <span className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: bg }}>
          <Icon size={14} style={{ color }} />
        </span>
      </div>
      <div className="font-serif text-3xl text-app">{value}</div>
    </div>
  );
}
